import { Add, Link as LinkIcon, Search, SearchOff } from "@mui/icons-material"
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material"
import { GridPaginationModel, GridSortModel } from "@mui/x-data-grid"
import { FC, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { CourierLinkItem, useGetLinksQuery } from "../../apis/appApi"
import { useAppSelector } from "../../hooks/hooks"
import { selectProfile } from "src/features/profile/profileSlice"
import { CourierLinksGrid } from "./CourierLinksGrid"
import { CreateLinkDialog } from "./CreateLinkDialog"
import { LinkDetailDrawer } from "./LinkDetailDrawer"

type StatusFilter = "all" | "active" | "pending" | "refused"

function getStatusParam(filter: StatusFilter): string | undefined {
  switch (filter) {
    case "active":
      return "ESTABLISHED"
    case "pending":
      return "INITIATED_WAITING_HAULER,INITIATED_WAITING_PROVIDER"
    case "refused":
      return "REFUSED"
    default:
      return undefined
  }
}

export const CourierLinksPage: FC = () => {
  const { t } = useTranslation()
  const profile = useAppSelector(selectProfile)
  const providerCode = profile?.provider?.code ?? ""

  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all")
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  })
  const [sortField, setSortField] = useState("id")
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc")
  const [selectedLinkId, setSelectedLinkId] = useState<number | null>(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)

  const { data: response, isLoading } = useGetLinksQuery({
    page: paginationModel.page + 1,
    pageSize: paginationModel.pageSize,
    search: search || undefined,
    sortField,
    sortOrder,
    status: getStatusParam(statusFilter),
  })

  const sortModel: GridSortModel = sortField
    ? [{ field: sortField, sort: sortOrder }]
    : []

  const handleSortModelChange = useCallback((model: GridSortModel) => {
    if (model.length > 0) {
      setSortField(model[0].field)
      setSortOrder(model[0].sort ?? "asc")
    } else {
      setSortField("id")
      setSortOrder("desc")
    }
    setPaginationModel((m) => ({ ...m, page: 0 }))
  }, [])

  const rows = response?.data ?? []
  const rowCount = response?.total ?? 0

  const selectedLink = rows.find((r) => r.id === selectedLinkId) ?? null

  const handleRowClick = useCallback((id: number) => {
    setSelectedLinkId(id)
  }, [])

  const handleDrawerClose = useCallback(() => {
    setSelectedLinkId(null)
  }, [])

  const handleAccept = useCallback((_link: CourierLinkItem) => {
    // Link list will auto-refresh via tag invalidation
  }, [])

  const handleDelete = useCallback((_link: CourierLinkItem) => {
    setSelectedLinkId(null)
  }, [])

  const handleCreate = useCallback(() => {
    setCreateDialogOpen(true)
  }, [])

  const handleCreateClose = useCallback(() => {
    setCreateDialogOpen(false)
  }, [])

  return (
    <Box
      data-testid="courier-links-page"
      sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}
    >
      {/* Page header */}
      <Box sx={{ px: { xs: 2, md: 3 }, pt: { xs: 2, md: 3 }, pb: 2 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "flex-start" }}
          spacing={1.5}
        >
          <Box>
            <Stack direction="row" spacing={1.5} alignItems="baseline">
              <Typography variant="h4" fontWeight={700}>
                {t("courierLinks.title")}
              </Typography>
              {!isLoading && (
                <Typography variant="body2" color="text.secondary">
                  {t("courierLinks.linksCount", { count: rowCount })}
                </Typography>
              )}
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 0.5 }}>
              <Typography variant="body1" color="text.secondary">
                {t("courierLinks.subtitle")}
              </Typography>
              {providerCode && (
                <Chip
                  label={`${t("courierLinks.yourCode")}: ${providerCode}`}
                  size="small"
                  variant="outlined"
                  data-testid="links-provider-code"
                />
              )}
            </Stack>
          </Box>
          <Button
            variant="contained"
            size="small"
            startIcon={<Add />}
            onClick={handleCreate}
            data-testid="links-create-btn"
            sx={{ flexShrink: 0 }}
          >
            {t("courierLinks.newLink")}
          </Button>
        </Stack>
      </Box>

      {/* Filters bar */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        spacing={2}
        sx={{ px: { xs: 2, md: 3 }, pb: 2 }}
      >
        <TextField
          size="small"
          placeholder={t("courierLinks.search")}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPaginationModel((m) => ({ ...m, page: 0 }))
          }}
          data-testid="links-search-input"
          sx={{ width: { xs: "100%", md: 280 } }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" sx={{ color: "text.disabled" }} />
                </InputAdornment>
              ),
            },
          }}
        />
        <ToggleButtonGroup
          value={statusFilter}
          exclusive
          onChange={(_e, val) => {
            if (val !== null) {
              setStatusFilter(val)
              setPaginationModel((m) => ({ ...m, page: 0 }))
            }
          }}
          size="small"
          data-testid="links-status-filter"
        >
          <ToggleButton value="all" data-testid="links-filter-all">
            {t("courierLinks.filterAll")}
          </ToggleButton>
          <ToggleButton value="active" data-testid="links-filter-active">
            {t("courierLinks.filterActive")}
          </ToggleButton>
          <ToggleButton value="pending" data-testid="links-filter-pending">
            {t("courierLinks.filterPending")}
          </ToggleButton>
          <ToggleButton value="refused" data-testid="links-filter-refused">
            {t("courierLinks.filterRefused")}
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: "hidden", px: { xs: 2, md: 3 }, pb: { xs: 2, md: 3 } }}>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            {rowCount === 0 && !search && statusFilter === "all" ? (
              <Stack
                data-testid="links-empty-state"
                alignItems="center"
                spacing={2}
                sx={{ maxWidth: 360 }}
              >
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    bgcolor: "grey.100",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <LinkIcon sx={{ fontSize: 36, color: "grey.400" }} />
                </Box>
                <Typography variant="h6" fontWeight={600} color="text.primary">
                  {t("courierLinks.empty.title")}
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  {t("courierLinks.empty.description")}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Add />}
                  onClick={handleCreate}
                >
                  {t("courierLinks.newLink")}
                </Button>
              </Stack>
            ) : (
              <Stack
                data-testid="links-empty-filter"
                alignItems="center"
                spacing={1.5}
                sx={{ maxWidth: 320 }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    bgcolor: "grey.100",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <SearchOff sx={{ fontSize: 28, color: "grey.400" }} />
                </Box>
                <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                  {t("courierLinks.empty.filtered")}
                </Typography>
              </Stack>
            )}
          </Box>
        ) : (
          <Paper
            variant="outlined"
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              borderRadius: 2,
            }}
          >
            <CourierLinksGrid
              rows={rows}
              loading={isLoading}
              rowCount={rowCount}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              onRowClick={handleRowClick}
              onAccept={handleAccept}
              onDelete={handleDelete}
            />
          </Paper>
        )}
      </Box>

      {/* Detail drawer */}
      <LinkDetailDrawer
        link={selectedLink}
        open={!!selectedLinkId}
        onClose={handleDrawerClose}
        onAccept={handleAccept}
        onDelete={handleDelete}
      />

      {/* Create dialog */}
      <CreateLinkDialog open={createDialogOpen} onClose={handleCreateClose} />
    </Box>
  )
}
